import { useEffect, useState } from "react";
import { useDistrictGuard } from "./useDistrictGuard";
import { getDistrictSetting, setDistrictSetting } from "./api";

const STORAGE_KEY = "pastoral.weeklyPlan.v1";
const SETTINGS_KEY = "weekly_plan";

// JS getDay() indices: 0=Domingo, 1=Lunes, ..., 6=Sábado
export const DAY_KEYS = [
  "domingo",
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
];

export const DAY_LABELS = {
  domingo: "Domingo",
  lunes: "Lunes",
  martes: "Martes",
  miercoles: "Miércoles",
  jueves: "Jueves",
  viernes: "Viernes",
  sabado: "Sábado",
};

export const DEFAULT_WEEKLY_PLAN = {
  domingo: "Clubes",
  lunes: "Descanso",
  martes: "Iglesia 1",
  miercoles: "Iglesia 2",
  jueves: "Iglesia 3",
  viernes: "Iglesia 4",
  sabado: "Shabbat",
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_WEEKLY_PLAN;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_WEEKLY_PLAN, ...parsed };
  } catch {
    return DEFAULT_WEEKLY_PLAN;
  }
}

export function useWeeklyPlan() {
  const { districtId } = useDistrictGuard();
  const [plan, setPlan] = useState(readStored);

  // Hydrate from Supabase on mount — remote wins over stale localStorage
  useEffect(() => {
    if (!districtId) return;
    getDistrictSetting(districtId, SETTINGS_KEY, null)
      .then(remote => {
        if (!remote) return;
        const merged = { ...DEFAULT_WEEKLY_PLAN, ...remote };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
        setPlan(merged);
      })
      .catch(() => {});
  }, [districtId]);

  // Cross-tab / cross-component sync via storage events
  useEffect(() => {
    const onChange = () => setPlan(readStored());
    window.addEventListener("weeklyPlan:updated", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("weeklyPlan:updated", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const update = (next) => {
    const merged = { ...DEFAULT_WEEKLY_PLAN, ...next };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    setPlan(merged);
    window.dispatchEvent(new Event("weeklyPlan:updated"));
    if (districtId) {
      setDistrictSetting(districtId, SETTINGS_KEY, merged).catch(() => {});
    }
  };

  const reset = () => update(DEFAULT_WEEKLY_PLAN);

  const getByGetDay = (idx) => plan[DAY_KEYS[idx]] || "";

  return { plan, update, reset, getByGetDay };
}
